import type { AxiosRequestConfig, AxiosHeaders, AxiosHeaderValue } from 'axios';
import axios from 'axios';
import { toast } from '@/hooks/use-toast';
import i18n from '@/i18n';
import { authAPI } from '@/lib/api/auth';
import logger from '@/lib/utils/logger';
// Kleine UUID-Fallback-Funktion, um externe Abhängigkeit zu vermeiden
const uuidv4 = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    try {
      return (crypto as any).randomUUID();
    } catch {}
  }
  // RFC4122 v4 Fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Resolve API base URL with robust HTTPS enforcement and env override
const resolvedApiBaseUrl = (() => {
  const envUrl = ((import.meta as any).env?.VITE_API_URL ?? undefined) as string | undefined;

  const isLocalhost = (() => {
    try {
      return (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      );
    } catch {
      return false;
    }
  })();

  // Note: sameOriginUrl currently ungenutzt; bewusst entfernt

  // Priority:
  // 1) Explicit env VITE_API_URL if provided (works for api subdomain setups)
  // 2) Localhost fallback
  // 3) Same-origin default (reverse-proxied /api/v1)
  // Hinweis: Host-Hilfsfunktionen entfernt; derzeit nicht benötigt

  // Default resolution
  let url = isLocalhost
    ? (envUrl?.trim() ? envUrl : 'http://localhost:8002/api/v1')
    : '/api/v1'; // Force strictly relative same-origin path in production

  // Normalize: remove trailing slashes
  if (url) {
    url = url.replace(/\/+$/, '');
  }

  // If current page is HTTPS but URL is HTTP, upgrade to HTTPS to avoid mixed content
  try {
    if (window.location.protocol === 'https:' && url?.startsWith('http://')) {
      url = 'https://' + url.substring('http://'.length);
    }
  } catch {
    // window might be unavailable in some build contexts; ignore
  }

  return url;
})();

const API_BASE_URL = resolvedApiBaseUrl;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add CSRF header (cookie-based auth)
apiClient.interceptors.request.use(
  (config) => {
    // Idempotency-Key automatisch setzen für mutierende Requests
    try {
      const method = (config.method || 'GET').toUpperCase();
      const isMutating = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
      if (isMutating) {
        const headers = (config.headers as AxiosHeaders);
        const hasKey = !!headers.get('Idempotency-Key');
        if (!hasKey) {
          headers.set('Idempotency-Key', uuidv4());
        }
      }
    } catch {
      // ignore
    }
    // Attach CSRF token from cookie if present
    const csrfMatch = /(?:^|; )csrf_token=([^;]+)/.exec(document.cookie);
  if (csrfMatch) {
      (config.headers as AxiosHeaders).set('X-CSRF-Token', decodeURIComponent(csrfMatch[1] || '') as AxiosHeaderValue);
    }
    
    // Don't set Content-Type for FormData - let browser set it with boundary
    if (config.data instanceof FormData) {
      (config.headers as AxiosHeaders).delete('Content-Type');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

// Response interceptor to handle refresh-and-retry before logout
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as (AxiosRequestConfig & { __isAuthRetry?: boolean });

    // Log/Toast mit Drosselung für Netzwerkfehler
    const url = (typeof error?.config?.url === 'string') ? error.config.url : undefined;
    const isAnalytics = !!url?.includes('/analytics/');
    // Fire-and-forget Telemetrie: Treffen oft auch unauthentifiziert ein, sollen aber NIE
    // einen Refresh- oder Logout-/Auth-Redirect auslösen. Read-Endpoints unter /analytics/
    // (z. B. /analytics/preferences/me, Admin-Insights) bleiben bewusst NICHT in dieser
    // Liste, damit dort 401 weiterhin den normalen Auth-Flow triggert.
    const isAnalyticsTelemetry = typeof url === 'string' && (
      /^\/analytics\/events\/(track|bulk)\b/.test(url) ||
      /^\/analytics\/sessions\b/.test(url) ||
      /^\/analytics\/errors\/log\b/.test(url)
    );
    const status = error.response?.status as number | undefined;
    const isAuthMe = typeof url === 'string' && /\/auth\/me\b/.test(url);
    const isAuthRefresh = typeof url === 'string' && /\/auth\/refresh\b/.test(url);
    
    // Public endpoints that should not trigger auth redirect on 401
    // These endpoints use optional auth - they work without authentication
    const isPublicEndpoint = typeof url === 'string' && (
      /^\/characters\/?(\?|$)/.test(url) ||           // GET /characters/
      /^\/characters\/\d+\/?(\?|$)/.test(url) ||      // GET /characters/:id
      /^\/characters\/\d+\/avatar\b/.test(url) ||     // GET /characters/:id/avatar
      url.startsWith("/media/avatars/") ||              // Avatar images
      url.startsWith("/media/gallery/") ||              // Gallery images
      /^\/creator-space\/creators\/?(\?|$)/.test(url) // GET /creator-space/creators
    );

    if (error.code === 'ERR_NETWORK') {
      // Unterdrücke laute Toasts für Analytics-Endpunkte, da diese durch Content-Blocker häufig geblockt werden
      if (!isAnalytics) {
        const now = Date.now();
        const lastKey = '__LAST_NETWORK_ERR__';
        const last = (window as any)[lastKey] as number | undefined;
        if (!last || now - last > 15000) {
          (window as any)[lastKey] = now;
          logger.error('Network Error: Backend is not reachable', API_BASE_URL);
          toast({ title: i18n.t('admin.errors.networkTitle', 'Netzwerkfehler'), description: i18n.t('admin.errors.networkUnavailable', 'Server nicht erreichbar.'), variant: 'destructive' });
        }
      } else {
        // Für Analytics nur auf Debug loggen, ohne Nutzer-Toast
        logger.debug?.('Analytics request blocked or unreachable (network)', { url });
      }
    } else if (error.response?.status !== 404) {
      if (!isAnalytics && !(status === 401 && isAuthMe)) {
        logger.error('API Error:', error.response?.status, error.response?.data);
      }
    }

    // Bei 401 zunächst einmalig Refresh versuchen und Original-Request wiederholen,
    // aber NICHT für /auth/me, öffentliche Endpunkte oder fire-and-forget Telemetrie,
    // um Endlosschleifen und ungewollte Logout/Redirects zu vermeiden.
    if (!isAuthMe && !isAuthRefresh && !isPublicEndpoint && !isAnalyticsTelemetry && status === 401 && originalRequest && !originalRequest.__isAuthRetry) {
      try {
        originalRequest.__isAuthRetry = true;
        await authAPI.refresh();
        return await apiClient.request(originalRequest);
      } catch (refreshError) {
        // Refresh fehlgeschlagen -> Logout/Redirect
        try {
          await authAPI.logout();
        } catch (_) {
          // ignore
        }
        if (!window.location.pathname.includes('/auth')) {
          window.location.href = '/auth';
        }
        throw error;
      }
    }
    
    // Für öffentliche Endpunkte und fire-and-forget Telemetrie: Bei 401 einfach den Fehler
    // werfen ohne Redirect. Die UI kann dann entscheiden, ob sie den Nutzer zum Login leitet;
    // Telemetrie-Fehler werden vom safeAnalyticsCall-Wrapper verschluckt.
    if ((isPublicEndpoint || isAnalyticsTelemetry) && status === 401) {
      throw error;
    }

    if (status && status >= 500) {
      if (!isAnalytics) {
        toast({ title: i18n.t('admin.errors.serverErrorTitle', 'Serverfehler'), description: i18n.t('admin.errors.tryAgainLater', 'Bitte später erneut versuchen.'), variant: 'destructive' });
      }
      throw error;
    }

    if (status && status >= 400) {
      // Keine Toasts/Logs für erwartetes 401 auf /auth/me (unauthenticated initial state)
      if (status === 401 && isAuthMe) {
        throw error;
      }

      // Spezifisch: Creator-Routen erlauben 401/403 ohne lauten Toast, da die UI Preview/Overlays zeigt
      const isCreatorGet = typeof url === 'string' && (
        // Profil- und Statistikabruf sind GETs, Fehler hier sollen leise sein
        /\/creator-space\/creators\/\d+\b/.test(url) ||
        /\/creator-space\/creators\/\d+\/posts\b/.test(url) ||
        /\/creator-space\/timeline\b/.test(url) ||
        /\/creator-space\/posts\/\d+\b/.test(url) ||
        /\/creator-space\/posts\/\d+\/comments\b/.test(url)
      );

      // 401/403 auf Creator-GETs: kein Toast (UI übernimmt Preview/Hinweise)
      const method = (error.config?.method || 'get').toString().toUpperCase();
      const isGet = method === 'GET';
      if (isCreatorGet && isGet && (status === 401 || status === 403)) {
        throw error;
      }

      // Spezifisch: Swipe-Routen – erwartete Zustände ohne Toast (UI zeigt Status)
      const isSwipeNext = typeof url === 'string' && /\/swipe\/next\b/.test(url);
      const isSwipeAction = typeof url === 'string' && /\/swipe\/action\b/.test(url);
      // /swipe/next: 404 (keine Profile) oder 402 (keine Swipes) -> leise behandeln
      if (isSwipeNext && (status === 404 || status === 402)) {
        throw error;
      }
      // /swipe/action: 400 (already swiped) oder 402 (keine Swipes/Superlikes) -> leise behandeln (UI übernimmt)
      if (isSwipeAction && (status === 400 || status === 402)) {
        throw error;
      }

      // Spezifisch: View-Endpoint darf 400 "Cannot record view" still sein
      const isCreatorViewPost = typeof url === 'string' && /\/creator-space\/posts\/\d+\/view\b/.test(url);
      if (isCreatorViewPost && status === 400) {
        throw error;
      }

      // Spezifisch: Extended Onboarding-Routen – 404 leise behandeln (UI zeigt nichts, wenn deaktiviert/keine Characters)
      const isExtendedOnboarding = typeof url === 'string' && /\/onboarding\/extended\b/.test(url);
      if (isExtendedOnboarding && status === 404) {
        throw error;
      }

      const detail = error.response?.data?.detail || error.message || i18n.t('admin.errors.requestFailed', 'Anfrage fehlgeschlagen');
      if (!isAnalytics) {
        toast({ title: i18n.t('admin.errors.errorTitle', 'Fehler'), description: String(detail), variant: 'destructive' });
      }
      throw error;
    }

    throw (error instanceof Error ? error : new Error(String(error)));
  }
);

apiClient.defaults.timeout = 15000;

// Optional: einfache, sehr begrenzte Retry-Logik nur für GET (ein Versuch)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as AxiosRequestConfig & { __isRetry?: boolean };
    if (!config || config.method?.toUpperCase() !== 'GET') {
      throw error;
    }
    if (!config.__isRetry && (error.code === 'ECONNABORTED' || error.message?.includes('timeout'))) {
      config.__isRetry = true;
      return apiClient.request(config);
    }
    throw error;
  }
);

// Helper function for API requests
export type ApiRequestOptions<Data = unknown> = {
  method?: AxiosRequestConfig['method'];
  data?: Data;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  timeout?: number;
};

export const apiRequest = async <TResponse = unknown, TData = unknown>(
  url: string,
  options: ApiRequestOptions<TData> = {}
): Promise<TResponse> => {
  const response = await apiClient.request({
    url,
    method: options.method ?? 'GET',
    data: options.data,
    headers: options.headers as any,
    params: options.params,
    timeout: (options.timeout ?? apiClient.defaults.timeout)!,
  });
  return (response.data as unknown) as TResponse;
};

export default apiClient;